import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Badge,
  IconAgent,
  IconClock,
  IconEnvelope,
  IconHelp,
  Link,
  SpacerHorizontal,
  SpacerVertical,
  Typography,
} from '@klarna/bubble-ui';
import { Flex } from '@klarna/mp-ui';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import { selectors as configSelectors } from 'mage-config';
import { withResponsive } from 'mage-components';
import { analytics } from 'moet-core';

import {
  getCountryMosEmail,
  getCountrySupportPhone,
  getKlarnaDotComSupportLink,
  getUserMerchantId,
  isMerchantOnboardingCompleted,
  isPreAssessmentMerchant,
  merchantRegion,
} from '@/selectors/merchant';
import {
  getPartnerCountrySupportEmail,
  getPartnerCountrySupportPhone,
  getPartnerCountryOpeningHours,
} from '@/selectors/options';
import { isManagedKyc, isUnmanagedKyc } from '@/selectors/widget';

const styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  contactContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  itemWithIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 15,
  },
  copyrightContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  midContainer: {
    minWidth: '160px',
    marginLeft: '10px',
  },
};

const ItemWithIcon = ({ children, icon: Icon }) => (
  <div style={styles.itemWithIcon}>
    <Icon colorToken="colors/text/neutral" />
    <SpacerHorizontal spaceToken="space/150" />
    {children}
  </div>
);

ItemWithIcon.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.func,
};

const tPrefix = 'products-fe.components.support_footer';

const SplitImageFooter = ({
  deploymentRegion,
  isManagedKyc,
  isMerchantOnboardingCompleted,
  isMobile,
  isPreAssessmentMerchant,
  merchantId = null,
  openingHours = undefined,
  showRecaptchaFooter,
  supportEmail = undefined,
  supportLink = undefined,
  supportPhone = undefined,
  t,
  termsAndConditions = '',
}) => {
  const showContactInfo = !isPreAssessmentMerchant && !isManagedKyc && merchantId;
  const currentYear = new Date().getFullYear();
  const MerchantID = () => (
    <div style={!isMobile ? styles.midContainer : {}}>
      <Badge
        backgroundColorToken="colors/backgrounds/subtle-inline"
        borderColorToken="colors/backgrounds/subtle-inline"
        textColorToken="colors/text/default"
      >
        {t(`${tPrefix}.merchant_id`, { merchantId })}
      </Badge>
    </div>
  );

  const ANALYTICS_EVENTS = {
    merchantSupportPhone: new analytics.Event({
      name: 'Support Phone Clicked',
      description: "Merchant clicks to call Klarna's merchant support",
      action: analytics.EVENT_ACTION.CTA_CLICK,
      category: analytics.EVENT_CATEGORY.FLOW_STEP,
      type: analytics.EVENT_TYPE.USER_PREFERENCE,
      properties: ['phoneNumber'],
    }),
    merchantSupportEmail: new analytics.Event({
      name: 'Support Email Clicked',
      description: "Merchant clicks to email Klarna's support",
      action: analytics.EVENT_ACTION.CTA_CLICK,
      category: analytics.EVENT_CATEGORY.FLOW_STEP,
      type: analytics.EVENT_TYPE.USER_PREFERENCE,
      properties: ['merchantSupportEmail'],
    }),
  };

  return (
    <Flex id="split-image-footer" style={styles.mainContainer}>
      <SpacerVertical spaceToken="space/300" />
      {merchantId && isMobile && (
        <>
          <MerchantID />
          <SpacerVertical spaceToken="space/150" />
        </>
      )}
      <div id="split-image-footer__contact" style={styles.contactContainer}>
        {showContactInfo && (
          <>
            {isMerchantOnboardingCompleted ? (
              <ItemWithIcon icon={IconHelp}>
                <Link
                  textToken="text-style/text/labels/body/medium"
                  href={supportLink}
                  target="_blank"
                >
                  {t(`${tPrefix}.support_link`)}
                </Link>
              </ItemWithIcon>
            ) : (
              <ItemWithIcon icon={IconEnvelope}>
                <Link
                  textToken="text-style/text/labels/body/medium"
                  href={`mailto:${supportEmail}`}
                  target="_self"
                  onPress={() =>
                    analytics.logEvent(ANALYTICS_EVENTS.merchantSupportEmail, {
                      merchantSupportEmail: supportEmail,
                    })
                  }
                >
                  {supportEmail}
                </Link>
              </ItemWithIcon>
            )}
            {supportPhone && (
              <ItemWithIcon icon={IconAgent}>
                <Link
                  textToken="text-style/text/labels/body/medium"
                  href={`tel:${supportPhone}`}
                  target="_self"
                  onPress={() =>
                    analytics.logEvent(ANALYTICS_EVENTS.merchantSupportPhone, {
                      phoneNumber: supportPhone,
                    })
                  }
                >
                  {supportPhone}
                </Link>
              </ItemWithIcon>
            )}
          </>
        )}
        {openingHours && <ItemWithIcon icon={IconClock}>{openingHours}</ItemWithIcon>}
      </div>
      <SpacerVertical spaceToken="space/150" />
      <div id="split-image-footer__copyright" style={styles.copyrightContainer}>
        <Typography
          textToken="text-style/text/paragraphs/tiny/regular"
          colorToken="colors/text/neutral"
        >
          {`${t(`${tPrefix}.copyright.${deploymentRegion}`, { currentYear })} `}
          <Link
            isUnderlined
            textToken="text-style/text/labels/tiny/regular"
            colorToken="colors/text/neutral"
            href={termsAndConditions}
            target="_blank"
          >
            {`${t(`${tPrefix}.terms_and_conditions`)}`}
          </Link>
          {` `}
          <Link
            isUnderlined
            textToken="text-style/text/labels/tiny/regular"
            colorToken="colors/text/neutral"
            href="/privacy-policy"
            target="_blank"
          >
            {t(`${tPrefix}.privacy_policy`)}
          </Link>
        </Typography>
        {merchantId && !isMobile && <MerchantID />}
      </div>
      {showRecaptchaFooter && <SpacerVertical spaceToken="space/150" />}
      {showRecaptchaFooter && (
        <div id="split-image-footer__recaptcha_note" style={styles.copyrightContainer}>
          <Typography
            textToken="text-style/text/paragraphs/tiny/regular"
            colorToken="colors/text/neutral"
          >
            {`${t(`${tPrefix}.recaptcha.1_main_text`)} `}
            <Link
              isUnderlined
              textToken="text-style/text/labels/tiny/regular"
              colorToken="colors/text/neutral"
              href="https://policies.google.com/privacy"
              target="_blank"
            >
              {t(`${tPrefix}.recaptcha.2_privacy_policy`)}
            </Link>
            {` ${t(`${tPrefix}.recaptcha.3_and`)} `}
            <Link
              isUnderlined
              textToken="text-style/text/labels/tiny/regular"
              colorToken="colors/text/neutral"
              href="https://policies.google.com/terms"
              target="_blank"
            >
              {t(`${tPrefix}.recaptcha.4_terms_of_service`)}
            </Link>
            {` ${t(`${tPrefix}.recaptcha.5_apply`)}`}
          </Typography>
        </div>
      )}
      <SpacerVertical spaceToken="space/300" />
    </Flex>
  );
};

SplitImageFooter.propTypes = {
  deploymentRegion: PropTypes.string.isRequired,
  isManagedKyc: PropTypes.bool.isRequired,
  isMerchantOnboardingCompleted: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isPreAssessmentMerchant: PropTypes.bool.isRequired,
  supportLink: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  merchantId: PropTypes.string,
  openingHours: PropTypes.string,
  showRecaptchaFooter: PropTypes.bool,
  supportEmail: PropTypes.string,
  supportPhone: PropTypes.string,
  termsAndConditions: PropTypes.string,
};

export default connect(state => {
  const language = i18nSelectors.language(state);

  return {
    t: translator(state),
    merchantId: getUserMerchantId(state),
    isManagedKyc: isManagedKyc(state),
    isMerchantOnboardingCompleted: isMerchantOnboardingCompleted(state),
    isPreAssessmentMerchant: isPreAssessmentMerchant(state),
    deploymentRegion: merchantRegion(state),
    supportEmail: !isUnmanagedKyc(state)
      ? getPartnerCountrySupportEmail(language)(state)
      : getCountryMosEmail(state),
    supportLink: getKlarnaDotComSupportLink(state),
    supportPhone: !isUnmanagedKyc(state)
      ? getPartnerCountrySupportPhone(language)(state)
      : getCountrySupportPhone(state),
    openingHours: !isUnmanagedKyc(state) ? getPartnerCountryOpeningHours(language)(state) : '',
    termsAndConditions: configSelectors.terms(state)?.url,
  };
})(withResponsive(SplitImageFooter));
